import React, { useState, useRef, useEffect } from 'react';
import { COLORS } from '../data/constants';

function formatVideoTime(seconds) {
  if (seconds == null || seconds < 0) return '0:00';
  const h = Math.floor(seconds / 3600);
  const m = Math.floor((seconds % 3600) / 60);
  const s = Math.floor(seconds % 60);
  if (h > 0) return `${h}:${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`;
  return `${m}:${String(s).padStart(2, '0')}`;
}

const UserMoments = ({
  moments,
  onAddMoment,
  onDeleteMoment,
  onEditMoment,
  onMomentClick,
  currentVideoTimestamp,
}) => {
  const [editingId, setEditingId] = useState(null);
  const [editText, setEditText] = useState('');
  const inputRef = useRef(null);

  useEffect(() => {
    if (editingId != null && inputRef.current) {
      inputRef.current.focus();
    }
  }, [editingId]);

  const startEditing = (moment) => {
    setEditingId(moment.id);
    setEditText(moment.note);
  };

  const commitEdit = () => {
    if (editingId != null) {
      onEditMoment(editingId, editText);
      setEditingId(null);
      setEditText('');
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') commitEdit();
    if (e.key === 'Escape') {
      setEditingId(null);
      setEditText('');
    }
  };

  return (
    <div style={styles.container}>
      <div style={styles.header}>
        <h3 style={styles.title}>Notes</h3>
        <button style={styles.addButton} onClick={onAddMoment}>
          + Add at {formatVideoTime(currentVideoTimestamp)}
        </button>
      </div>

      {moments.length === 0 ? (
        <div style={styles.empty}>
          Pause the video and click "Add" to mark moments with notes.
        </div>
      ) : (
        <div style={styles.list}>
          {moments.map((moment) => (
            <div
              key={moment.id}
              style={styles.card}
              onClick={() => onMomentClick(moment)}
            >
              <div style={styles.cardTop}>
                <span style={styles.timeBadge}>
                  {moment.videoTimestampFormatted}
                </span>
                <span style={styles.probability}>
                  {(moment.probability * 100).toFixed(1)}%
                </span>
                <button
                  style={styles.deleteButton}
                  onClick={(e) => {
                    e.stopPropagation();
                    onDeleteMoment(moment.id);
                  }}
                >
                  ×
                </button>
              </div>

              {editingId === moment.id ? (
                <input
                  ref={inputRef}
                  style={styles.noteInput}
                  value={editText}
                  onChange={(e) => setEditText(e.target.value)}
                  onBlur={commitEdit}
                  onKeyDown={handleKeyDown}
                  placeholder="Add a note..."
                  onClick={(e) => e.stopPropagation()}
                />
              ) : (
                <div
                  style={styles.noteText}
                  onClick={(e) => {
                    e.stopPropagation();
                    startEditing(moment);
                  }}
                >
                  {moment.note || 'Click to add note...'}
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

const styles = {
  container: {
    backgroundColor: '#141722',
    borderRadius: '12px',
    padding: '20px',
    border: '1px solid rgba(255,255,255,0.06)',
  },
  header: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: '16px',
  },
  title: {
    fontSize: '15px',
    fontWeight: '600',
    color: '#fff',
    margin: 0,
  },
  addButton: {
    padding: '6px 14px',
    fontSize: '12px',
    fontWeight: '600',
    color: COLORS.PRIMARY_BLUE,
    backgroundColor: 'rgba(43, 151, 212, 0.12)',
    border: '1px solid rgba(43, 151, 212, 0.25)',
    borderRadius: '6px',
    cursor: 'pointer',
    transition: 'all 0.15s ease',
    fontFamily: 'inherit',
  },
  empty: {
    fontSize: '13px',
    color: '#6b7280',
    textAlign: 'center',
    padding: '24px 0',
  },
  list: {
    display: 'flex',
    flexDirection: 'column',
    gap: '8px',
    maxHeight: '360px',
    overflowY: 'auto',
  },
  card: {
    backgroundColor: 'rgba(255,255,255,0.04)',
    borderRadius: '8px',
    padding: '10px 12px',
    cursor: 'pointer',
    transition: 'background-color 0.15s ease',
    border: '1px solid rgba(255,255,255,0.06)',
  },
  cardTop: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    marginBottom: '6px',
  },
  timeBadge: {
    fontSize: '11px',
    fontWeight: '700',
    color: COLORS.PRIMARY_BLUE,
    backgroundColor: 'rgba(43, 151, 212, 0.15)',
    padding: '2px 8px',
    borderRadius: '4px',
    fontFamily: 'monospace',
  },
  probability: {
    fontSize: '13px',
    fontWeight: '600',
    color: '#e5e7eb',
    flex: 1,
  },
  deleteButton: {
    width: '22px',
    height: '22px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: '16px',
    color: '#6b7280',
    backgroundColor: 'transparent',
    border: 'none',
    borderRadius: '4px',
    cursor: 'pointer',
    lineHeight: 1,
    padding: 0,
    fontFamily: 'inherit',
  },
  noteText: {
    fontSize: '13px',
    color: '#9ca3af',
    lineHeight: '1.4',
    cursor: 'text',
    fontStyle: 'italic',
  },
  noteInput: {
    width: '100%',
    fontSize: '13px',
    color: '#e5e7eb',
    backgroundColor: 'rgba(255,255,255,0.06)',
    border: '1px solid rgba(43, 151, 212, 0.3)',
    borderRadius: '4px',
    padding: '6px 8px',
    outline: 'none',
    fontFamily: 'inherit',
    lineHeight: '1.4',
  },
};

export default UserMoments;
